import type { ReactNode, Ref } from "react";
import { Loader2, Lock, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  /** A send is in flight: spinner on the button, sends are held, the field stays editable. */
  sending?: boolean;
  /** Hard lock (maintenance, a full chat): field and buttons are disabled. */
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  /** Rendered between the field and the send button (e.g. the concierge's alerts bell). */
  accessory?: ReactNode;
  /** Rendered before the field (e.g. Sage's attach button). */
  leading?: ReactNode;
  /** The field's name for screen readers. */
  label?: string;
  /** Writing is not possible here: this message replaces the composer (request not accepted, chat closed). */
  locked?: ReactNode;
  /** The field itself, for surfaces that fill it and place the cursor (Sage's ready-made requests). */
  inputRef?: Ref<HTMLTextAreaElement>;
  /** Pad for the home-indicator inset, for composers that reach the screen edge. */
  safeArea?: boolean;
  autoFocus?: boolean;
  className?: string;
}

/**
 * The one message composer for every chat (src/components/chat). Enter sends,
 * Shift+Enter breaks the line. The field is never disabled just because a
 * send is pending, that would blur it and drop the on-screen keyboard after
 * every message. Near the length limit it says how much room is left.
 */
const ChatComposer = ({
  value,
  onChange,
  onSend,
  sending = false,
  disabled = false,
  placeholder,
  maxLength = 2000,
  rows = 1,
  accessory,
  leading,
  label = "Message",
  locked,
  inputRef,
  safeArea = false,
  autoFocus = false,
  className,
}: Props) => {
  const canSend = !disabled && !sending && value.trim().length > 0;
  const submit = () => {
    if (canSend) onSend();
  };
  if (locked) {
    return (
      <div className={cn("shrink-0 border-t px-4 py-3 text-sm text-muted-foreground flex items-center gap-2", className)}>
        <Lock className="h-4 w-4 shrink-0" aria-hidden />
        <span>{locked}</span>
      </div>
    );
  }
  const left = maxLength - value.length;
  return (
    <div
      className={cn(
        "shrink-0 border-t px-4 py-3 flex items-end gap-2",
        safeArea && "pb-[max(0.75rem,env(safe-area-inset-bottom))]",
        className,
      )}
    >
      {leading}
      <div className="flex-1 min-w-0">
        <Textarea
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            // Not while an input method is composing a character (Chinese, Japanese, accents).
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-label={label}
          // A message: plain text keyboard with corrections, no AutoFill, and
          // the return key says "send" (Felipe, Oct 20).
          autoComplete="off"
          autoCorrect="on"
          autoCapitalize="sentences"
          enterKeyHint="send"
          // Messenger look (Felipe, Oct 18: the orange ring "looks bad"): a soft
          // filled box with a thin edge; focus only darkens the edge, no ring.
          className="rounded-[22px] resize-none min-h-[44px] max-h-40 text-[15px] border bg-muted/60 border-border px-4 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-foreground/25"
        />
        {left <= Math.max(20, Math.round(maxLength * 0.1)) && (
          <p className="mt-1 text-[11px] text-muted-foreground" aria-live="polite">{left} characters left</p>
        )}
      </div>
      {accessory}
      <Button
        type="button"
        className="rounded-full h-11 w-11 shrink-0 p-0"
        onClick={submit}
        disabled={!canSend}
        aria-label="Send"
      >
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      </Button>
    </div>
  );
};

export default ChatComposer;
